import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LEGAL_DOCS } from '../legal/content';
import LanguageSwitcher from '../components/LanguageSwitcher';

// route doc key → i18n key (imprint/privacy/terms) + canonical English path
const DOC_META = {
  impressum: { i18nKey: 'imprint', path: '/imprint' },
  datenschutz: { i18nKey: 'privacy', path: '/privacy' },
  agb: { i18nKey: 'terms', path: '/terms' },
};

/**
 * /imprint, /privacy, /terms (German aliases /impressum, /datenschutz, /agb)
 * — the legal pages required to sell to German businesses
 * (plan_b2b_self_serve.md §5). Page chrome is i18n'd (EN/DE); the document
 * BODIES are deliberately German-only for now: they are contracts and
 * statutory notices under German law, where German is the binding language.
 * Non-German readers get an explanatory note. Canonical source (incl. the
 * AVV template): docs/Scan2Play/legal/.
 */
const LegalPage = ({ doc }) => {
  const { t, i18n } = useTranslation('legal');
  const entry = LEGAL_DOCS[doc];
  const meta = DOC_META[doc];
  const isGerman = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('de');

  useEffect(() => {
    if (meta) document.title = `${t(`titles.${meta.i18nKey}`)} — Okio`;
    return () => {
      document.title = 'Okio';
    };
  }, [meta, t]);

  if (!entry || !meta) return null;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/business">
            <img src="/logo_white.webp" alt="OKIO" className="h-5 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-5 text-xs text-white/40">
              {Object.entries(DOC_META).map(([docKey, docMeta]) => (
                <Link
                  key={docKey}
                  to={docMeta.path}
                  className={doc === docKey ? 'text-white' : 'hover:text-white transition-colors'}
                >
                  {t(`nav.${docMeta.i18nKey}`)}
                </Link>
              ))}
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        {!isGerman && (
          <div className="mb-8 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-white/50 text-xs leading-relaxed">{t('germanNote')}</p>
          </div>
        )}
        <article
          className="prose prose-invert prose-sm max-w-none
            prose-headings:tracking-tight prose-h1:text-3xl prose-h2:text-xl
            prose-h2:mt-10 prose-h3:text-base prose-a:text-emerald-400
            prose-li:text-white/70 prose-p:text-white/70 prose-strong:text-white"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.body}</ReactMarkdown>
        </article>
      </main>
    </div>
  );
};

export default LegalPage;
